import StatusTag from "common/Tags/StatusTag";
import InfoCircleOutlined from "@ant-design/icons/InfoCircleOutlined";
import Popover from "common/Popover/Popover";
import React, { CSSProperties, useContext, useState } from "react";
import { useTheme } from "styled-components";
import { useIPWhitelistTag } from "@shared/features/configuration/hooks";
import IntercomLink from "common/Text/IntercomLink";
import { HelperTooltip } from "common/Tooltips/HelperTooltip.tsx";
import { AnalyticsPageName, LanguageNameSpaces } from "@shared/types";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface WhitelistTagProps {
  style?: CSSProperties;
  withBgColor?: boolean;
  animated?: boolean;
  exchange?: string;
}

const WhitelistTag = ({
  style,
  withBgColor,
  animated,
  exchange,
}: WhitelistTagProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { isDrawer } = useContext(DrawerContext) || {};
  const appTheme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const tagData = useIPWhitelistTag(isDrawer, exchange);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const statusTag = (
    <StatusTag
      text={
        <>
          {tagData.tagText}
          <InfoCircleOutlined
            style={{
              marginLeft: 8,
              color: appTheme.colors.primaryBlue,
            }}
          />
        </>
      }
      color={appTheme.colors.primaryBlue}
      style={style}
      withBgColor={withBgColor}
      animated={animated}
    />
  );

  if (isBottomNavigator) {
    return (
      <HelperTooltip
        title={t("whitelist.not_ip_whitelisted")}
        description={tagData.popoverTexts}
        articleId={tagData.articleId}
        triggerElement={statusTag}
        analyticsPageName={AnalyticsPageName.ACCOUNT_CONFIGURATION}
        tooltipStyle={{ maxWidth: 400, width: 400 }}
        placement="topRight"
        containerStyle={{ margin: 0 }}
      />
    );
  }

  return (
    <Popover
      placement="topRight"
      open={tooltipOpen}
      onOpenChange={(visible) => setTooltipOpen(visible)}
      content={
        <div onClick={(e) => e.stopPropagation()}>
          {tagData.popoverTexts.map((text, index) => (
            <div key={index}>{text}</div>
          ))}
          {!!tagData.articleId && (
            <IntercomLink
              articleId={tagData.articleId}
              style={{ display: "block", fontStyle: "italic" }}
            >
              {t("whitelist.learn_more")}
            </IntercomLink>
          )}
        </div>
      }
      styles={{ root: { maxWidth: 400 } }}
    >
      <div onMouseEnter={() => setTooltipOpen(true)}>{statusTag}</div>
    </Popover>
  );
};

export default WhitelistTag;
